<template>
  <div
    class="home-group-wrapper"
    style="padding-left: 0 !important; padding-right: 0 !important;"
  >
    <PatientFilter :showPatientBox="false" />
    <Helper />
    <div class="care-plus-dashboard-content">
      <el-row :gutter="30">
        <el-col :md="24" :lg="15">
          <div class="care-plus-dashboard-section take-action-section">
            <div class="care-plus-dashboard-section-title">
              <span>{{ $t('text.take_action').toUpperCase() }}</span>
              <span class="items-count">{{ takeActionPatients.length }}</span>
            </div>
            <div class="care-plus-dashboard-details">
              <!-- if less than 5 patient, then don't scroll -->
              <div
                :class="takeActionPatients.length > 5 ? 'scroll-wrapper' : ''"
              >
                <div v-if="takeActionPatients.length > 5">
                  <div
                    class="scroll-btn v-h-center top"
                    @click.stop="prev(true)"
                  >
                    <i class="el-icon-caret-top"></i>
                  </div>
                  <div
                    class="scroll-btn v-h-center bottom"
                    @click.stop="next(true)"
                  >
                    <i class="el-icon-caret-bottom"></i>
                  </div>
                </div>
                <el-carousel
                  ref="leftCarousel"
                  height="340px"
                  direction="vertical"
                  :autoplay="false"
                  v-loading="loading"
                >
                  <el-empty
                    :description="$t('text.no_data')"
                    v-if="takeActionPatients.length === 0"
                  >
                  </el-empty>
                  <el-carousel-item
                    v-else
                    v-for="pageNo in getPageCounts(true)"
                    :key="'left' + pageNo"
                  >
                    <div class="patient-list">
                      <div
                        v-for="item in getPaginatedPatients(pageNo, true)"
                        :key="item.id"
                      >
                        <PatientItem :patient="item" />
                      </div>
                    </div>
                  </el-carousel-item>
                </el-carousel>
              </div>
              <CompletionTime
                :taskCompletionLevel="taskCompletionLevel"
                :totalPadCycles="totalPadCycles"
                :requiredPadCycles="requiredPadCycles"
              />
            </div>
          </div>
        </el-col>
        <el-col :md="24" :lg="9">
          <div class="care-plus-dashboard-section monitor-section">
            <div class="care-plus-dashboard-section-title">
              <span>{{ $t('text.monitor') }}</span>
              <span class="items-count">{{ monitorPatients.length }}</span>
            </div>
            <div class="care-plus-dashboard-details">
              <div :class="monitorPatients.length > 5 ? 'scroll-wrapper' : ''">
                <div v-if="monitorPatients.length > 5">
                  <div
                    class="scroll-btn v-h-center top"
                    @click.stop="prev(false)"
                  >
                    <i class="el-icon-caret-top"></i>
                  </div>
                  <div
                    class="scroll-btn v-h-center bottom"
                    @click.stop="next(false)"
                  >
                    <i class="el-icon-caret-bottom"></i>
                  </div>
                </div>
                <el-carousel
                  ref="rightCarousel"
                  height="340px"
                  direction="vertical"
                  :autoplay="monitorPatients.length > 5"
                  v-loading="loading"
                >
                  <el-empty
                    :description="$t('text.no_data')"
                    v-if="monitorPatients.length === 0"
                  >
                  </el-empty>
                  <el-carousel-item
                    v-else
                    v-for="pageNo in getPageCounts(false)"
                    :key="'right' + pageNo"
                  >
                    <div class="patient-list">
                      <div
                        v-for="item in getPaginatedPatients(pageNo, false)"
                        :key="item.id"
                      >
                        <PatientItem :patient="item" />
                      </div>
                    </div>
                  </el-carousel-item>
                </el-carousel>
              </div>
              <RecentEventsSection :recentEvents="recentEvents" />
            </div>
          </div>
        </el-col>
      </el-row>
      <TeamWorkSection
        :overallScore="overallScore"
        :improvements="improvements"
        :nodes="nodes"
        :totalPadCycles="totalPadCycles"
        :requiredPadCycles="requiredPadCycles"
        :loading="getTeamScoreLoading"
      />
    </div>
    <div class="demo-mode" v-if="isDemoOrg">
      {{ $t('text.demonstration_mode') }}
    </div>
  </div>
</template>

<script>
import { actionType, monitorType, batteryStatus } from '@/const.js'
import ApiService from '@/services/ApiService.js'
import _ from 'lodash'
import Helper from '@/components/Helper.vue'
import PatientFilter from '@/components/dashboard/PatientFilter.vue'
import PatientItem from '@/components/carePlusDashboard/PatientItem.vue'
import TeamWorkSection from '@/components/carePlusDashboard/TeamWorkSection.vue'
import CompletionTime from '@/components/carePlusDashboard/CompletionTime.vue'
import RecentEventsSection from '@/components/carePlusDashboard/RecentEventsSection.vue'
import moment from 'moment'
import swal from 'sweetalert'
import { MessageBox } from 'element-ui'

export default {
  data() {
    return {
      actionType: actionType,
      monitorType: monitorType,
      batteryStatus: batteryStatus,
      loading: false,
      responseData: [],
      checkNodeNetworkTimer: null,
      takeActionPatients: [],
      monitorPatients: [],
      itemsPerPage: 5,
      overallScore: 5,
      improvements: [],
      getTeamScoreLoading: false,
      taskCompletionLevel: 0,
      recentEvents: [],
      refreshDataTimer: null,
      displayNodeNetworkError: false,
      autoSwitchTakeActionTimer: null,
      nodes: [],
      totalPadCycles: 0,
      requiredPadCycles: 0
    }
  },
  components: {
    Helper,
    PatientFilter,
    PatientItem,
    TeamWorkSection,
    CompletionTime,
    RecentEventsSection
  },
  mounted() {
    this.getData()
    this.getTeamScore()

    let frequency = this.isDemoOrg ? 3 * 60 * 1000 : 15 * 60 * 1000
    this.refreshDataTimer = setInterval(() => {
      this.getData(true)
      this.getTeamScore(true)
    }, frequency)
    this.$hub.$on('patient-updated', this.onPatientUpdated)
    this.$hub.$on('patient-status-updated', this.onPatientStatusUpdated)
    this.$hub.$on('patient-moved', this.onPatientMoved)
    this.resetAutoSwitchTakeActionTimer()

    // check node network
    this.checkNodeNetwork()
    this.checkNodeNetworkTimer = setInterval(this.checkNodeNetwork, 900000)
  },
  computed: {
    isDemoOrg() {
      let user = localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user'))
        : false
      const selectedOrganisation = user.organisations.filter(
        item => item.Active
      )
      if (selectedOrganisation && selectedOrganisation.length) {
        return selectedOrganisation[0].OrganisationName.IsDemo
      }
      return false
    }
  },
  watch: {
    '$store.state.filter.dashboardFilter': {
      handler: _.debounce(function() {
        this.getData()
        this.getTeamScore()
      }, 100),
      deep: true
    }
  },
  methods: {
    prev(fromTakeActionSection = true) {
      if (fromTakeActionSection) {
        this.$refs.leftCarousel.prev()
        this.resetAutoSwitchTakeActionTimer()
      } else {
        this.$refs.rightCarousel.prev()
      }
    },
    next(fromTakeActionSection = true) {
      if (fromTakeActionSection) {
        this.$refs.leftCarousel.next()
        this.resetAutoSwitchTakeActionTimer()
      } else {
        this.$refs.rightCarousel.next()
      }
    },
    // In take action panel, if there are more than 5 patients and user scroll to the bottom patient and leave it there, it doesn't scroll back up automatically. So the highest 5 should always have priority, if user is not scrolling and has been idle for 30 seconds, it should scroll back up to the top 5.
    resetAutoSwitchTakeActionTimer() {
      clearTimeout(this.autoSwitchTakeActionTimer)
      this.autoSwitchTakeActionTimer = setTimeout(() => {
        this.$refs.leftCarousel.setActiveItem(0)
      }, 30000)
    },
    getPageCounts(fromTakeActionSection = true, returnActual = false) {
      let data = fromTakeActionSection
        ? this.takeActionPatients
        : this.monitorPatients
      let number = Math.ceil(data.length / this.itemsPerPage)
      if (returnActual) return number

      // If there are only 2 pages, the switching effect of the carousel plug-in is not ideal, so repeat it twice and display it as 4 pages
      return number === 2 ? 4 : number
    },
    getPaginatedPatients(pageNo, fromTakeActionSection = true) {
      let actualPageCounts = this.getPageCounts(fromTakeActionSection, true)
      // If there are only 2 pages, the switching effect of the carousel plug-in is not ideal, so repeat it twice and display it as 4 pages
      if (actualPageCounts === 2) {
        if (pageNo === 3) {
          pageNo = 1
        } else if (pageNo === 4) {
          pageNo = 2
        }
      }
      let data = fromTakeActionSection
        ? this.takeActionPatients
        : this.monitorPatients
      const start = (pageNo - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      const paginatedPatients = data.slice(start, end)
      while (paginatedPatients.length < this.itemsPerPage) {
        paginatedPatients.push({ id: null, placeholder: true })
      }
      return paginatedPatients
    },
    checkNodeNetwork() {
      if (this.isDemoOrg) return false
      ApiService.CheckNodeNetwork(
        this.$store.getters[`user/selecteOrganisationId`]
      )
        .then(response => {
          if (response.data.success) {
            if (!response.data.data.networkHealthy) {
              this.showNodeNetworkError()
            }
          } else {
            this.showNodeNetworkError()
          }
        })
        .catch(() => {
          this.showNodeNetworkError()
        })
    },
    showNodeNetworkError() {
      if (this.displayNodeNetworkError) return false // if node network already display, then no need display again.
      let warningIconUrl = require('@/assets/image/warning-icon/warning.svg')
      this.displayNodeNetworkError = true
      MessageBox.confirm(
        `<div class="warning-content">${this.$t(
          'text.node_network_error_msg'
        )}.</div> <img class='warning-icon' src=${warningIconUrl}>`,
        this.$t('text.warning'),
        {
          confirmButtonText: this.$t('text.ok'),
          confirmButtonClass: 'confirm-btn',
          type: 'warning',
          customClass: 'delete-warning-box',
          showClose: true,
          closeOnClickModal: false,
          dangerouslyUseHTMLString: true,
          showCancelButton: false
        }
      ).then(() => {
        this.displayNodeNetworkError = false
      })
    },
    onPatientUpdated(response) {
      let isAdded = true
      this.responseData.forEach((item, index) => {
        if (item.patientId === response.patientId) {
          isAdded = false
          this.responseData.splice(index, 1, {
            patientId: item.patientId,
            alias: response.alias,
            roomNumber: response.roomNumber,
            data: {
              ...item.data,
              podMac: response.podMac
            }
          })
        }
      })
      if (isAdded) {
        if (!response.data) {
          response.data = {}
        }
        this.responseData.push(response)
      }
      this.handleData()
    },
    onPatientStatusUpdated(response) {
      this.responseData.forEach((item, index) => {
        // The messageOrderNumber attribute is the sequence attribute carried by the signalR service
        // In order to solve the problem that the signalR service does not send messages in the correct order
        if (
          item.patientId === response.patientId &&
          (!item.messageOrderNumber ||
            item.messageOrderNumber < response.messageOrderNumber)
        ) {
          this.responseData.splice(index, 1, {
            ...item,
            data: {
              ...response.data,
              isUpgraded: item.data.hasOwnProperty('isUpgraded')
                ? item.data.isUpgraded
                : true // signalr service don't return isUpgraded field, so preserved the value in API
            },
            messageOrderNumber: response.messageOrderNumber || 0
          })
          this.handleData()
        }
      })
    },
    onPatientMoved(data) {
      this.responseData.forEach((item, index) => {
        if (item.patientId === data.patientId) {
          this.responseData.splice(index, 1)
          this.handleData()
        }
      })
    },
    getTeamScore(dontLoading = false) {
      if (!dontLoading) {
        this.getTeamScoreLoading = true
      }
      ApiService.GetTeamScore(
        this.$store.getters[`filter/calculatedFilter`].organisationId,
        this.$store.getters[`filter/calculatedFilter`].flag,
        this.$store.getters[`filter/calculatedFilter`].filterIds
      )
        .then(response => {
          this.getTeamScoreLoading = false
          if (response.data.success) {
            let data = response.data.data
            this.overallScore = data.OverallScore <= 5 ? data.OverallScore : 5
            this.improvements = data.Improvements.slice(0, 3)
            this.taskCompletionLevel =
              data.TaskCompletionLevel <= 100 ? data.TaskCompletionLevel : 100
            this.recentEvents = data.RecentEvents
            this.nodes = data.Nodes ? data.Nodes : []
            this.totalPadCycles = data.TotalPadCycles
            this.requiredPadCycles = data.RequiredPadCycle
            this.$nextTick(() => {
              this.adjustImproveItemsHeight()
            })
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: this.$t('text.failed'),
              text: response.data.message
            })
          }
        })
        .catch(() => {
          this.getTeamScoreLoading = false
        })
    },
    adjustImproveItemsHeight() {
      const elements = document.querySelectorAll('.improve-item')
      let maxHeight = 0

      elements.forEach(el => {
        if (el.clientHeight > maxHeight) {
          maxHeight = el.clientHeight
        }
      })

      elements.forEach(el => {
        el.style.height = `${maxHeight}px`
      })
    },
    getData(dontLoading = false) {
      if (!dontLoading) {
        this.loading = true
      }
      ApiService.GetAllPodsStatus(
        this.$store.getters[`filter/calculatedFilter`].organisationId,
        this.$store.getters[`filter/calculatedFilter`].flag,
        this.$store.getters[`filter/calculatedFilter`].filterIds
      )
        .then(response => {
          this.responseData = response.data
          this.loading = false
          this.handleData()
        })
        .catch(() => {
          this.loading = false
        })
    },
    handleData() {
      this.calcTakeActionSection()
      this.calcMonitorSection()
    },
    calcTakeActionSection() {
      let actions = this.responseData.filter(item => {
        return this.needTakeAction(item)
      })
      // priority should be: attend fall, change pad, other
      // system should sort again by statusChangeTime
      actions = actions.map(item => {
        // default priority is 0
        item.statusChangeTime = moment().diff(
          moment.utc(item.data.padCycleStartTime),
          'seconds'
        )
        item.priority = 0
        if (item.data.isLikelyInBin) {
          // isLikelyInBin status have a highest priority
          item.priority = 3
          item.warnings = [
            {
              type: this.actionType.check_bin
            }
          ]
        } else if (item.data.fallStatus === 'RED') {
          item.priority = 2
          item.warnings = [
            {
              type: this.actionType.check_fall
            }
          ]
        } else if (item.data.podStatus === 'LOWBATTERY') {
          item.warnings = [
            {
              type: this.actionType.lowbattery
            }
          ]
        } else if (item.data.podStatus === 'FLATBATTERY') {
          item.warnings = [
            {
              type: this.actionType.flatbattery
            }
          ]
        } else if (item.data.podStatus === 'DISCONNECTED') {
          item.warnings = [
            {
              type: this.actionType.pod_unclipped
            }
          ]
        } else if (item.data.podStatus === 'CHECKCONNECTION') {
          item.warnings = [
            {
              type: this.actionType.check_connection
            }
          ]
        } else if (item.data.podStatus === 'SENSORDISCONNECTED') {
          item.warnings = [
            {
              type: this.actionType.sensor_disconnected
            }
          ]
        } else if (item.data.podStatus === 'NODATARECEIVED') {
          item.warnings = [
            {
              type: this.actionType.no_data_received
            }
          ]
        } else if (
          item.data.podStatus === 'RED' ||
          item.data.podStatus === 'MAXCAPACITY'
        ) {
          item.priority = 1
          // MAXCAPACITY behave same as RED
          item.warnings = [
            {
              type: this.actionType.check_pad
            }
          ]
        } else if (item.data.padStatus === 'RED') {
          item.priority = 1
          item.warnings = [
            {
              type: this.actionType.check_time_in_pad
            }
          ]
        } else {
          //other warnings
          item.warnings = []
          if (item.data.positionStatus === 'RED') {
            item.warnings.push({
              type: this.actionType.check_position
            })
          }
          if (item.data.temperatureStatus === 'RED') {
            item.warnings.push({
              type: this.actionType.check_temperature
            })
          }
        }
        return {
          ...item,
          isAlert: true
        }
      })
      //order by redStartTime
      this.sortArray(actions, 'redStartTime', 'asc')
      //order by priority
      this.sortArray(actions, 'priority')
      this.takeActionPatients = actions
    },
    calcMonitorSection() {
      let monitors = this.responseData.filter(item => {
        return !this.needTakeAction(item)
      })
      monitors = monitors.map(item => {
        item.warnStatus = item.data.podStatus
        if (item.data.positionStatus === 'YELLOW') {
          item.warnStatus === 'YELLOW'
        }
        return {
          ...item,
          statusNumber: monitorType[item.warnStatus],
          isAlert: false
        }
      })
      // sort by redStartTime
      this.sortArray(monitors, 'redStartTime', 'asc')
      //green status shown in front
      this.sortArray(monitors, 'statusNumber')
      this.monitorPatients = monitors
    },
    needTakeAction(item) {
      // isLikelyInBin status have a highest priority
      if (item.data && item.data.isLikelyInBin) return true
      return (
        item.data &&
        (item.data.podStatus === 'RED' ||
          item.data.podStatus === 'MAXCAPACITY' ||
          item.data.positionStatus === 'RED' ||
          item.data.temperatureStatus === 'RED' ||
          item.data.podStatus === 'DISCONNECTED' ||
          item.data.podStatus === 'CHECKCONNECTION' ||
          item.data.podStatus === 'SENSORDISCONNECTED' ||
          item.data.podStatus === 'NODATARECEIVED' ||
          item.data.podStatus === 'LOWBATTERY' ||
          item.data.podStatus === 'FLATBATTERY' ||
          item.data.padStatus === 'RED' ||
          item.data.fallStatus === 'RED') &&
        !['ANALYSING', 'PADNOTSELECTED', 'PADUNKNOWN'].includes(
          item.data.podStatus
        )
      )
    },
    sortArray(array, orderBy, direction = 'desc') {
      array.sort((a, b) => {
        let valA = a[orderBy]
        let valB = b[orderBy]

        if (orderBy === 'redStartTime') {
          let dateA = Date.parse(valA)
          let dateB = Date.parse(valB)
          valA = isNaN(dateA) ? 0 : dateA
          valB = isNaN(dateB) ? 0 : dateB

          if (valA === 0 && valB !== 0) return 1
          if (valB === 0 && valA !== 0) return -1
        }

        if (valA < valB) {
          return direction === 'asc' ? -1 : 1
        } else if (valA > valB) {
          return direction === 'asc' ? 1 : -1
        } else {
          return 0
        }
      })
    }
  },
  beforeDestroy() {
    clearInterval(this.checkNodeNetworkTimer)
    clearInterval(this.refreshDataTimer)
    clearInterval(this.autoSwitchTakeActionTimer)
  }
}
</script>

<style lang="scss">
.offline-nodes-warning {
  .el-notification__group {
    width: 100%;
    margin-left: 5px;
  }
  .el-notification__title,
  .el-icon-warning {
    color: #dd868f;
  }
  .el-icon-warning {
    font-size: 20px;
  }
  .el-notification__content {
    text-align: left;
  }
  .el-notification__closeBtn {
    top: 15px;
  }
}
</style>
